// @ts-nocheck

import React, { cloneElement, useState, useEffect, useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import useDesktopHorizontalScroll from '../../hooks/useDesktopHorizontalScroll';
import { getDimensions } from '../../lib/core/dom/dimensions';
import { isLibrary } from '../../lib/useragent';
import useWinabetLibContext from '../../winabet-lib/hooks/useWinabetLibContext';

type StickyProps = {
  children: React.ReactNode;
  disabled?: boolean;
  offsetTop?: number;
  zIndex?: number;
};

const eventToListen = [
  'resize',
  'scroll',
  'mousewheel', // add mousewheel in addition of `scroll` for IE to work properly
  'touchstart',
  'touchmove',
  'touchend',
  'pageshow',
  'load',
  'click',
];

const Wrap = styled.div`
  padding-bottom: ${(p) => p.$paddingBottom}px;
`;

const InnerContent = styled.div`
  ${({ $isStickyAndNotDisabled, $zIndex, $width, $offsetTop, $left }) => {
    if ($isStickyAndNotDisabled) {
      return css`
        position: fixed;
        width: ${$width}px;
        top: ${$offsetTop}px;
        left: ${$left}px;
        z-index: ${$zIndex};
      `;
    }
  }};
`;

const Sticky = ({ children, disabled = false, offsetTop = 0, zIndex = 1 }: StickyProps) => {
  const placeholderRef = useRef(null);
  const childrenRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [heightState, setHeightState] = useState(0);
  const isStickyAndNotDisabled = isSticky && !disabled;
  const winabetLibContext = useWinabetLibContext();
  const topShift = winabetLibContext?.sharedData?.SCENE_HEADER_HEIGHT || offsetTop;

  const getDistanceFromTop = () => {
    if (placeholderRef.current) {
      return placeholderRef.current.getBoundingClientRect().top;
    }
    return 0;
  };

  const getHeight = () => {
    if (childrenRef.current) {
      return childrenRef.current.getBoundingClientRect().height;
    }

    return 0;
  };

  const getIsSticky = useCallback(() => {
    const contentHeight = getHeight();
    const distanceFromTop = getDistanceFromTop();
    const thresholdReached = distanceFromTop - offsetTop <= 0;
    const couldBeSticky = window.innerHeight > contentHeight + offsetTop;
    return couldBeSticky && thresholdReached;
  }, [offsetTop]);

  const recomputeState = useCallback(() => {
    const isStickyCurrentStatus = getIsSticky();
    const height = getHeight();
    if (isSticky !== isStickyCurrentStatus || heightState !== height) {
      setIsSticky(isStickyCurrentStatus);
      setHeightState(height);
    }
  }, [heightState, getIsSticky, isSticky]);

  const on = useCallback(() => {
    if (!Target.__MOBILE__) {
      if (isLibrary) {
        const { parentRef } = winabetLibContext;
        eventToListen.forEach((event) => parentRef?.current?.addEventListener(event, recomputeState));
      } else {
        eventToListen.forEach((event) => window.addEventListener(event, recomputeState));
      }
    }
  }, [recomputeState, winabetLibContext]);

  const off = useCallback(() => {
    if (!Target.__MOBILE__) {
      if (isLibrary) {
        const { parentRef } = winabetLibContext;
        eventToListen.forEach((event) => parentRef?.current?.removeEventListener(event, recomputeState));
      } else {
        eventToListen.forEach((event) => window.removeEventListener(event, recomputeState));
      }
    }
  }, [recomputeState, winabetLibContext]);

  useEffect(() => {
    if (disabled) {
      setIsSticky(false);
    } else {
      on();

      return () => {
        recomputeState();
        off();
      };
    }
  }, [disabled, off, on, recomputeState]);

  const replaceHorizontal = useCallback(() => {
    if (placeholderRef.current) {
      const { left } = placeholderRef.current.getBoundingClientRect();
      if (childrenRef.current) {
        childrenRef.current.style.left = `${left}px`;
      }
    }
  }, []);

  // replaces the Sticky video at its correct position after the wheel event occures
  useDesktopHorizontalScroll(replaceHorizontal);

  const left = placeholderRef.current ? getDimensions(placeholderRef.current).left : 0;

  // When we are in sticky, the right column height is 0. To have the height, the trick is to set a padding-bottom
  const placeHolderStyle = { paddingBottom: 0 };
  if (isSticky && !disabled) {
    placeHolderStyle.paddingBottom = heightState;
  }

  return (
    <Wrap $paddingBottom={placeHolderStyle.paddingBottom} ref={placeholderRef}>
      <InnerContent
        $zIndex={zIndex}
        $offsetTop={topShift}
        $left={left}
        $width={placeholderRef.current?.clientWidth}
        $isStickyAndNotDisabled={isStickyAndNotDisabled}
        ref={childrenRef}
        data-testid="sticky"
      >
        {cloneElement(children, { offsetTop, isSticky, placeholderRef })}
      </InnerContent>
    </Wrap>
  );
};

export default Sticky;

type withStickyProps = {
  hasStickyContent: boolean;
  disabled?: boolean;
  zIndex?: number;
};

/**
 * HOC which returns an enhanced component with a sticky placeholder or not
 *
 * @param {Node} WrappedComponent the component which will be enhanced
 */
export const withSticky = (WrappedComponent: unknown) => {
  return function AnonymousComponent(props: withStickyProps) {
    const { hasStickyContent, disabled = false, zIndex, ...rest } = props;

    return (
      <>
        {hasStickyContent ? (
          <Sticky disabled={disabled} zIndex={zIndex}>
            <WrappedComponent {...rest} />
          </Sticky>
        ) : (
          <WrappedComponent {...rest} />
        )}
      </>
    );
  };
};
